import { AfterViewInit, ChangeDetectorRef, Component, ElementRef, EventEmitter, Input, OnChanges, Output, SimpleChanges } from '@angular/core';
import {NgClass, NgForOf, NgIf} from "@angular/common";
import {distinctUntilChanged, filter, Subscription, take} from "rxjs";
import {Store} from "@ngrx/store";
import {AppState} from "../../../state/app.state";
import * as TagActions from "../../../state/tag/tag.actions";
import {
  selectAllTags, selectAttachTagFailure,
  selectAttachTagSuccess,
  selectTagsError,
  selectTagsLoading
} from "../../../state/tag/tag.selectors";
import {PopupService} from "../../../services/popup.service";

@Component({
  selector: 'app-report-tags',
  standalone: true,
  imports: [
    NgForOf,
    NgClass,
    NgIf
  ],
  templateUrl: './report-tags.component.html',
  styleUrl: './report-tags.component.scss'
})
export class ReportTagsComponent implements AfterViewInit, OnChanges {

  @Output() heightChange = new EventEmitter<number>();

  @Input() tagsOpen: boolean = false;

  @Input() reportId: number;
  @Input() reportTags: any;
  @Output() tagsOpenChange = new EventEmitter<boolean>();

  // Emit selected filters to the parent component
  @Output() selectedFiltersChange = new EventEmitter<{ category: string, value: string, label: string }[]>();

  tagsSubscription: Subscription;
  errorSubscription: Subscription;
  loadingSubscription: Subscription;
  tags: any = {};  // Placeholder for tags data

  loading: boolean = false;

  filterOptions = {
    roles: [],
    dealStages: [],
    dealValues: [],
    statuses: [],
    closeDates: [],
    myJudgement: [],
    icpName: []
  };

  selectedFilters: { [key: string]: string[] } = {
    roles: [],
    dealStages: [],
    dealValues: [],
    statuses: [],
    closeDates: [],
    myJudgement: [],
    icpName: []
  };

  constructor(
    private store: Store<AppState>,
    private popupService: PopupService,
    private elRef: ElementRef,
    private cdr: ChangeDetectorRef
  ) {}

  ngAfterViewInit() {
    // console.log('[report-tags] ngAfterViewInit')
    this.updateHeight();
  }

  // Emit the current height of the component
  emitHeight() {
    const height = this.elRef.nativeElement.offsetHeight;
    this.heightChange.emit(height);
  }

  // Method to update height, call this whenever you need to re-emit the height
  updateHeight() {
    // console.log('[report-tags] updateHeight')

    // Use setTimeout to ensure the DOM has finished rendering
    setTimeout(() => {
      this.emitHeight();
    });
  }

  ngOnInit(): void {
    this.getTags();
  }

  ngOnDestroy(): void {
    if (this.tagsSubscription) {
      this.tagsSubscription.unsubscribe();
    }
    if (this.errorSubscription) {
      this.errorSubscription.unsubscribe();
    }
    if (this.loadingSubscription) {
      this.loadingSubscription.unsubscribe();
    }
  }

  // Called when the component's input properties change
  ngOnChanges(changes: SimpleChanges): void {
    if (changes['reportTags'] && changes['reportTags'].currentValue) {
      this.populateSelectedFilters(changes['reportTags'].currentValue);
    }
  }

  // Populate selectedFilters based on reportTags input
  populateSelectedFilters(tagsData: any[]): void {
    // Reset all categories to empty arrays
    for (let category in this.selectedFilters) {
      this.selectedFilters[category] = [];
    }

    if (!tagsData || tagsData.length === 0) {
      return;
    }

    // Loop through each tag in the reportTags and populate selectedFilters
    tagsData.forEach(tag => {
      const category = this.toCamelCase(tag.type); // Ensure the 'type' matches your filterOptions keys
      const value = tag.slug;

      if (this.selectedFilters[category]) {
        this.selectedFilters[category].push(value);
      }
    });

    this.emitSelectedFilters();
  }

  toCamelCase(str: string): string {
    return str
      .replace(/[-_\s]+(.)?/g, (match, chr) => chr ? chr.toUpperCase() : '')
      .replace(/^[A-Z]/, match => match.toLowerCase());
  }

  getTags(): void {
    // console.log('getTags')

    this.store.dispatch(TagActions.loadTags());

    this.loadingSubscription = this.store.select(selectTagsLoading)
      .pipe(
        filter(loading => !loading),
        take(1)
      )
      .subscribe(() => {
        this.tagsSubscription = this.store.select(selectAllTags)
          .pipe(
            distinctUntilChanged(),
            // take(1)
          )
          .subscribe((tags: any) => {
            if (tags && typeof tags === 'object') {
              this.filterOptions = {
                roles: this.mapTagOptions(tags.roles || []),
                dealStages: this.mapTagOptions(tags.dealStages || []),
                dealValues: this.mapTagOptions(tags.dealValues || []),
                statuses: this.mapTagOptions(tags.statuses || []),
                closeDates: this.mapTagOptions(tags.closeDates || []),
                myJudgement: this.mapTagOptions(tags.myJudgement || []),
                icpName: this.mapTagOptions(tags.icpName || [])
              };
              // Emit height after tags are loaded
              this.updateHeight();
            }
          });
      });

    this.errorSubscription = this.store.select(selectTagsError)
      .pipe(filter(error => !!error), take(1))
      .subscribe(error => {
        console.error('Failed to load tags:', error);
      });
  }

  mapTagOptions(tags: any[]): { label: string, value: string }[] {
    return tags.map(tag => ({
      label: tag.name?.en || '',
      value: tag.slug?.en || ''
    }));
  }

  toggleTags(): void {
    console.log('toggleTags')

    this.tagsOpen = !this.tagsOpen;
    this.tagsOpenChange.emit(this.tagsOpen);

    // Update the height after toggling
    this.updateHeight();
  }

  /*
  onFilterChange(category: string, value: string, event: any): void {
    if (event.target.checked) {
      this.selectedFilters[category].push(value);
    } else {
      this.selectedFilters[category] = this.selectedFilters[category].filter(v => v !== value);
    }
    this.emitSelectedFilters();

    // Create a variable with all the categories and values selected based on selectedFilters
    const selectedTags = this.createSelectedTagsObject();
    console.log(selectedTags); // Log it to see the result or use it as needed

    // Attach Tags
    this.attachTags(selectedTags.tags)

    // Emit height after filter change if it affects the height
    this.updateHeight();
  }
  */

  onFilterChange(category: string, value: string, event: any): void {
    // Clone the array for immutability and avoid modifying frozen objects
    let selectedCategoryFilters = [...(this.selectedFilters[category] || [])];

    if (event.target.checked) {
      // Push the new value to the cloned array
      selectedCategoryFilters.push(value);
    } else {
      // Remove the value if it's unchecked
      selectedCategoryFilters = selectedCategoryFilters.filter(v => v !== value);
    }

    // Replace the category with the updated cloned array in selectedFilters
    this.selectedFilters = {
      ...this.selectedFilters,  // Clone the object to avoid direct mutation
      [category]: selectedCategoryFilters // Update the specific category array
    };

    // Emit the updated filters
    this.emitSelectedFilters();

    // Create a variable with all the categories and values selected based on selectedFilters
    const selectedTags = this.createSelectedTagsObject();
    console.log(selectedTags); // Log it to see the result or use it as needed

    // Attach Tags
    this.attachTags(selectedTags.tags);

    // Emit height after filter change if it affects the height
    this.updateHeight();
  }


  // Method to create the desired object structure from selectedFilters
  createSelectedTagsObject(): any {
    const tagsObject = {
      tags: {
        roles: [],
        dealStages: [],
        dealValues: [],
        statuses: [],
        closeDates: [],
        myJudgement: [],
        icpName: []
      }
    };

    // Loop through each category in selectedFilters
    for (const category in this.selectedFilters) {
      if (this.selectedFilters[category].length > 0) {
        // Add category and its selected values to the tags object
        tagsObject.tags[category] = this.selectedFilters[category];
      }
    }

    return tagsObject;
  }

  removeFilter(category: string, value: string): void {
    // Filter out the specified value from the category array
    this.selectedFilters[category] = this.selectedFilters[category].filter(v => v !== value);

    // Keep the category with an empty array if no items remain
    if (this.selectedFilters[category].length === 0) {
      this.selectedFilters[category] = [];
    }

    this.emitSelectedFilters();
    this.updateHeight(); // Update the height after removing a filter
  }

  getActiveFilters(): { category: string, value: string, label: string }[] {
    let activeFilters: { category: string, value: string, label: string }[] = [];
    for (let category in this.selectedFilters) {
      const options = this.filterOptions[category];
      const labels = this.selectedFilters[category].map(value => {
        const option = options.find(o => o.value === value);
        return option ? { category, value, label: option.label } : null;
      }).filter(item => item !== null);
      activeFilters = activeFilters.concat(labels);
    }
    return activeFilters;
  }

  // Emit the current selected filters to the parent component
  emitSelectedFilters(): void {
    this.selectedFiltersChange.emit(this.getActiveFilters());
  }

  isSelected(category: string, value: string): boolean {
    return this.selectedFilters[category].includes(value);
  }

  attachTags(tags: any): void {

    this.loading = true;

    const payload = {
      searchId: this.reportId,
      tags: tags,
    };

    console.log('payload', payload)

    // Dispatch the attachTag action with the payload
    this.store.dispatch(TagActions.attachTag(payload));

    // Subscribe to success and failure in store
    this.store.select(selectAttachTagSuccess).pipe(
      filter(success => !!success), // Only proceed if success is true
      take(1)
    ).subscribe(() => {
      this.popupService.notify('Tags successfully attached!', 'Tag attached', 'success');
      this.loading = false;
    });

    this.store.select(selectAttachTagFailure).pipe(
      filter(error => !!error), // Only proceed if there is an error
      take(1)
    ).subscribe(error => {
      this.popupService.notify('Failed to attach tags. Please try again.', 'Tag attached', 'error');
      this.loading = false;
    });

  }

}
